import React, { useEffect, useRef, useState } from 'react';

const FencePDFViewer = ({ station, fences, onFenceClick, onCanvasClick, isDrawingMode }) => {
  const canvasRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [pdfPage, setPdfPage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile Detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // PDF laden
  useEffect(() => {
    const loadPDF = async () => {
      try {
        if (!window.pdfjsLib) {
          console.error('PDF.js nicht geladen');
          return;
        }

        // PDF.js Worker konfigurieren
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

        // PDF-Datei für Station laden
        const pdfUrl = `/uploads/${station} 07.05.2024.pdf`;
        console.log(`🔍 Lade PDF: ${pdfUrl}`);

        const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        
        setPdfPage(page);
        setPdfLoaded(true);
        console.log(`✅ PDF für Station ${station} geladen`);
        
        renderPDF(page);
      } catch (error) {
        console.error('❌ Fehler beim Laden der PDF:', error);
      }
    };

    loadPDF();
  }, [station]);

  // PDF rendern
  const renderPDF = async (page) => {
    if (!page || !pdfCanvasRef.current) return;

    const viewport = page.getViewport({ scale });
    const canvas = pdfCanvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    await page.render(renderContext).promise;
    
    // Canvas für Bauzäune auf gleiche Größe setzen
    if (canvasRef.current) {
      canvasRef.current.width = viewport.width;
      canvasRef.current.height = viewport.height;
      drawFences();
    }
  };

  // Bauzäune zeichnen
  const drawFences = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Bauzäune für diese Station zeichnen
    const stationFences = fences.filter(fence => fence.station === station);
    
    stationFences.forEach(fence => {
      // Rechteck für Bauzaun
      ctx.fillStyle = fence.color || '#ffc107';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      
      const x = fence.x * scale;
      const y = fence.y * scale;
      const width = fence.width * scale;
      const height = fence.height * scale;
      
      ctx.fillRect(x, y, width, height);
      ctx.strokeRect(x, y, width, height);
      
      // Nummern anzeigen
      ctx.fillStyle = '#000';
      ctx.font = `${12 * scale}px Arial`;
      ctx.textAlign = 'center';
      
      const fenceNumber = fence.number_to ? 
        `${fence.number_from}-${fence.number_to}` : 
        fence.number_from;
      
      ctx.fillText(fenceNumber, x + width / 2, y + height / 2 + 4);
      
      // Typ anzeigen (kurz/normal)
      if (fence.is_short) {
        ctx.fillStyle = '#dc3545';
        ctx.font = `${8 * scale}px Arial`;
        ctx.fillText('KURZ', x + width / 2, y - 5);
      }
    });
  };

  // Canvas Click Handler
  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    // Prüfen ob auf einen Bauzaun geklickt wurde
    const clickedFence = fences.find(fence => 
      fence.station === station &&
      x >= fence.x && x <= fence.x + fence.width &&
      y >= fence.y && y <= fence.y + fence.height
    );

    if (clickedFence && onFenceClick) {
      // Auf Mobile: zeige Eingabedialog für Nummern
      if (isMobile) {
        handleMobileFenceEdit(clickedFence);
      } else {
        onFenceClick(clickedFence);
      }
    } else if (isDrawingMode && onCanvasClick && !isMobile) {
      // Zeichnen nur auf Desktop
      onCanvasClick({ x: Math.round(x), y: Math.round(y) });
    }
  };

  // Mobile Bauzaun-Bearbeitung
  const handleMobileFenceEdit = (fence) => {
    const fenceRange = fence.number_to ? 
      `${fence.number_from}-${fence.number_to}` : 
      fence.number_from;
      
    const newNumbers = prompt(
      `Bauzaun-Nummern bearbeiten:\n\nAktuell: ${fenceRange}\n\nFormat:\n- Einzeln: "005"\n- Bereich: "005-008"`,
      fenceRange
    );
    
    if (newNumbers && newNumbers !== fenceRange) {
      const parts = newNumbers.includes('-') ? newNumbers.split('-') : [newNumbers];
      const updatedFence = {
        ...fence,
        number_from: parts[0].trim(),
        number_to: parts[1] ? parts[1].trim() : ''
      };
      
      if (onFenceClick) {
        onFenceClick(updatedFence, true); // Flag für Mobile-Update
      }
    }
  };

  // Zoom-Funktionen
  const zoomIn = () => {
    const newScale = Math.min(scale * 1.2, 3.0);
    setScale(newScale);
    if (pdfPage) renderPDF(pdfPage);
  };

  const zoomOut = () => {
    const newScale = Math.max(scale / 1.2, 0.5);
    setScale(newScale);
    if (pdfPage) renderPDF(pdfPage);
  };

  const resetZoom = () => {
    setScale(1.0);
    if (pdfPage) renderPDF(pdfPage);
  };

  // Bauzäune neu zeichnen wenn sich Daten ändern
  useEffect(() => {
    if (pdfLoaded) {
      drawFences();
    }
  }, [fences, scale, pdfLoaded]);

  return (
    <div className="fence-pdf-viewer">
      {/* Zoom Controls */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="btn-group btn-group-sm">
          <button className="btn btn-outline-secondary" onClick={zoomOut}>
            <i className="bi bi-zoom-out"></i>
          </button>
          <button className="btn btn-outline-secondary" onClick={resetZoom}>
            {Math.round(scale * 100)}%
          </button>
          <button className="btn btn-outline-secondary" onClick={zoomIn}>
            <i className="bi bi-zoom-in"></i>
          </button>
        </div>
        
        <div className={`text-muted ${isMobile ? 'small' : ''}`}>
          {isMobile ? 
            'Tippen Sie auf einen Bauzaun zum Bearbeiten' :
            (isDrawingMode ? 
              'Klicken Sie auf eine freie Stelle, um einen Bauzaun zu platzieren' : 
              'Klicken Sie auf einen Bauzaun, um ihn zu bearbeiten'
            )
          }
        </div>
      </div>

      {/* PDF + Canvas Container */}
      <div 
        style={{ 
          position: 'relative', 
          border: '1px solid #ddd', 
          overflow: 'auto',
          maxHeight: '70vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        {/* PDF Canvas (Hintergrund) */}
        <canvas
          ref={pdfCanvasRef}
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            zIndex: 1 
          }}
        />
        
        {/* Bauzaun Canvas (Vordergrund) */}
        <canvas
          ref={canvasRef}
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            zIndex: 2,
            cursor: isDrawingMode ? 'crosshair' : 'pointer'
          }}
          onClick={handleCanvasClick}
        />

        {/* Loading Indicator */}
        {!pdfLoaded && (
          <div 
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '400px' }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Lädt...</span>
              </div>
              <p className="text-muted">
                <i className="bi bi-file-earmark-pdf me-2"></i>
                Lade PDF für Station {station}...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FencePDFViewer;